import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Layers, Filter, Search, ArrowLeft, ExternalLink } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProcessedAssets = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    search: ''
  });

  useEffect(() => {
    fetchAssets();
  }, [filters.type, filters.status]);

  const fetchAssets = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);

      const response = await axios.get(
        `${API}/admin/processed-assets?${params.toString()}`,
        { withCredentials: true }
      );
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch processed assets:', error);
      toast.error('Failed to load processed assets');
    } finally {
      setLoading(false);
    }
  };

  const filteredAssets = assets.filter(asset => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        asset.id.toLowerCase().includes(searchLower) ||
        (asset.content_source_id && asset.content_source_id.toLowerCase().includes(searchLower))
      );
    }
    return true;
  });

  const getStatusColor = (status) => {
    const colors = {
      processing: '#FFA500',
      ready: '#29E07F',
      error: '#FF4F4F'
    };
    return colors[status] || '#8A93A6';
  };

  const getTypeIcon = (type) => {
    const icons = {
      cleaned_video: '🎬',
      clip: '✂️',
      merged_clip: '🔗',
      optimized_variant: '⚡'
    };
    return icons[type] || '📦';
  };

  return (
    <div className="p-8 space-y-8" data-testid="processed-assets-admin">
      <div className="flex items-center gap-4">
        <Link to="/admin" className="p-2 rounded-lg hover:bg-white/5 transition-colors">
          <ArrowLeft size={24} className="text-white" />
        </Link>
        <div className="flex-1">
          <h1 className="text-4xl font-bold text-white mb-2">Processed Assets</h1>
          <p className="text-[#8A93A6] text-lg">View all processed content outputs</p>
        </div>
        <Layers size={32} className="text-[#39E6C8]" />
      </div>

      {/* Filters */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Filter size={20} className="text-[#39E6C8]" />
          <h2 className="text-lg font-bold text-white">Filters</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#8A93A6] mb-2">Type</label>
            <Select value={filters.type} onValueChange={(val) => setFilters({...filters, type: val})}>
              <SelectTrigger className="w-full" data-testid="filter-type">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="cleaned_video">Cleaned Video</SelectItem>
                <SelectItem value="clip">Clip</SelectItem>
                <SelectItem value="merged_clip">Merged Clip</SelectItem>
                <SelectItem value="optimized_variant">Optimized Variant</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#8A93A6] mb-2">Status</label>
            <Select value={filters.status} onValueChange={(val) => setFilters({...filters, status: val})}>
              <SelectTrigger className="w-full" data-testid="filter-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#8A93A6] mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93A6]" size={18} />
              <Input
                type="text"
                placeholder="Search by ID"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Processed Assets List */}
      <div className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-6">
          {filteredAssets.length} Processed Asset{filteredAssets.length !== 1 ? 's' : ''}
        </h2>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-[#39E6C8] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="h-64 flex items-center justify-center border-2 border-dashed border-white/10 rounded-xl">
            <p className="text-[#8A93A6]">No processed assets found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Type</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Resolution</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Aspect Ratio</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Platform</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Duration</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Source ID</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Asset ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.map((asset) => (
                  <tr
                    key={asset.id}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`asset-row-${asset.id}`}
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{getTypeIcon(asset.type)}</span>
                        <span className="text-white text-sm">{asset.type.replace('_', ' ')}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${getStatusColor(asset.status)}20`,
                          color: getStatusColor(asset.status)
                        }}
                      >
                        {asset.status}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-[#8A93A6]">{asset.resolution || '-'}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-[#8A93A6]">{asset.aspect_ratio || '-'}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-[#8A93A6]">{asset.platform_target || '-'}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-[#8A93A6]">
                        {asset.duration_seconds
                          ? `${Math.floor(asset.duration_seconds / 60)}:${(asset.duration_seconds % 60).toString().padStart(2, '0')}`
                          : '-'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      {asset.content_source_id ? (
                        <Link
                          to={`/admin/content-sources`}
                          className="flex items-center gap-1 text-[#3A7BFF] hover:text-[#39E6C8] transition-colors"
                        >
                          <code className="text-xs bg-white/5 px-2 py-1 rounded">
                            {asset.content_source_id.substring(0, 8)}
                          </code>
                          <ExternalLink size={12} />
                        </Link>
                      ) : (
                        <span className="text-[#8A93A6]">-</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <code className="text-xs text-[#8A93A6] bg-white/5 px-2 py-1 rounded">
                        {asset.id.substring(0, 8)}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessedAssets;
